import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Upload, Save, ImageIcon, Sparkles, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'osc_admin_token';
const MAX_IMAGE_BYTES = 900 * 1024;

export default function OfferManager() {
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [ctaLabel, setCtaLabel] = useState('હમણાં જ બુક કરો');
  const [endAt, setEndAt] = useState(''); // datetime-local string
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/offer`, { headers: authHeaders() });
      setActive(!!data.active);
      setTitle(data.title || '');
      setDetails(data.details || '');
      setPrice(data.price || '');
      setOriginalPrice(data.original_price || '');
      setCtaLabel(data.cta_label || 'હમણાં જ બુક કરો');
      // Convert ISO to datetime-local format YYYY-MM-DDTHH:mm
      if (data.end_at) {
        try {
          const d = new Date(data.end_at);
          if (!isNaN(d.getTime())) {
            const pad = (n) => String(n).padStart(2, '0');
            setEndAt(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
          } else setEndAt('');
        } catch { setEndAt(''); }
      } else setEndAt('');
      setImageDataUrl(data.image_data_url || '');
    } catch (e) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 900;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        let q = 0.85;
        let dataUrl = canvas.toDataURL('image/jpeg', q);
        while (dataUrl.length > MAX_IMAGE_BYTES && q > 0.4) { q -= 0.1; dataUrl = canvas.toDataURL('image/jpeg', q); }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast({ title: 'Only image files allowed' }); return; }
    try {
      const dataUrl = await compressImage(f);
      setImageDataUrl(dataUrl);
      toast({ title: 'Image ready', description: `${Math.round(dataUrl.length / 1024)} KB` });
    } catch { toast({ title: 'Image processing failed' }); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const endAtISO = endAt ? new Date(endAt).toISOString() : '';
      await axios.put(`${API}/admin/offer`, {
        active, title, details, price,
        original_price: originalPrice,
        cta_label: ctaLabel,
        image_data_url: imageDataUrl,
        end_at: endAtISO,
      }, { headers: authHeaders() });
      toast({ title: 'Offer saved', description: active ? 'Popup is LIVE' : 'Popup is OFF' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <Card className="card-dark p-4 md:p-5 mt-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-400" />
            <h2 className="text-base md:text-lg font-bold text-white">Homepage Offer Popup</h2>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">Toggle ON to show a popup with image, prices and Book button</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[11px] font-semibold ${active ? 'text-green-400' : 'text-gray-500'}`}>{active ? 'LIVE' : 'OFF'}</span>
          <Switch checked={active} onCheckedChange={setActive} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Diwali Mega Offer" className="bg-[#14141c] border-purple-900/60 text-white mt-1 h-9 text-sm" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Details</Label>
            <Textarea value={details} onChange={e => setDetails(e.target.value)} placeholder="Short description..." rows={3} className="bg-[#14141c] border-purple-900/60 text-white mt-1 text-sm min-h-[70px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Original Price</Label>
              <Input value={originalPrice} onChange={e => setOriginalPrice(e.target.value)} placeholder="e.g. 6,800" className="bg-[#14141c] border-purple-900/60 text-white mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Offer Price</Label>
              <Input value={price} onChange={e => setPrice(e.target.value)} placeholder="e.g. 3,999" className="bg-[#14141c] border-purple-900/60 text-white mt-1 h-9 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Button Label</Label>
            <Input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="હમણાં જ બુક કરો" className="bg-[#14141c] border-purple-900/60 text-white mt-1 h-9 text-sm" />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Offer Ends At (countdown timer)</Label>
            <Input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="bg-[#14141c] border-purple-900/60 text-white mt-1 h-9 text-sm" />
            <p className="text-[10px] text-gray-500 mt-1">Leave empty for no timer. Popup auto-hides after this time.</p>
          </div>
        </div>

        <div>
          <Label className="text-gray-300 text-xs">Offer Image</Label>
          <div className="mt-1 rounded-xl border border-dashed border-purple-900/60 bg-[#14141c] p-3">
            {imageDataUrl ? (
              <div className="relative">
                <img src={imageDataUrl} alt="offer" className="w-full h-40 object-cover rounded-lg" />
                <button onClick={() => setImageDataUrl('')} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 flex items-center justify-center text-red-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-gray-500 text-xs">
                <ImageIcon className="w-7 h-7 mb-1 opacity-60" />
                No image selected
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="mt-2 w-full h-9 text-sm border-purple-900/60 text-gray-200 hover:bg-purple-950/30">
              <Upload className="w-3.5 h-3.5 mr-2" /> Upload / Change Image
            </Button>
            <p className="text-[10px] text-gray-500 mt-1.5">Auto-compressed · JPEG · max 900×900</p>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-purple-900/40 pt-4">
        <div className="text-[11px] text-gray-400">Shows once per browser session. Toggle OFF to hide.</div>
        <Button onClick={save} disabled={saving} className="gradient-btn rounded-full h-10 px-6 font-semibold w-full sm:w-auto text-sm">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Offer'}
        </Button>
      </div>
    </Card>
  );
}
